import { isNotUndefined, isUndefined } from 'ramda-adjunct';
import { findFirstValueDefined } from '@dirootie-toolbox/objects';
import { normalizePath } from '../../packages/core/toolbox/objects/paths/normalize';
import {
  all,
  has,
  hasPath,
  map,
  prop,
  either,
  complement,
  pathEq,
  propEq,
  assocPath,
} from 'ramda';
import { templateTypeList } from '../_types';

export const hasAll = (paths: any[]) => (obj: any) => {
  const nPaths = map(normalizePath, paths);
  const result = all((x) => hasPath(x, obj), nPaths);
  return result;
};

export const ifPresentEq =
  (path: string | string[], value: any) => (obj: any) => {
    const normalizedPath = normalizePath(path);
    const result = either(
      complement(hasPath(normalizedPath)),
      pathEq(normalizedPath, value)
    )(obj);
    return result;
  };

export type PartitionFilter<T> = {
  filter: (obj: any) => boolean;
  prop: T;
};

export type PartitionBy<TKeys extends string | symbol | number> = (
  filters: PartitionFilter<TKeys>[]
) => { [key in TKeys]: any[] };

export const partitionBy =
  <T extends string | symbol | number>(filters: PartitionFilter<T>[]) =>
  (objs: any[]) => {
    return objs.reduce((acc, obj) => {
      for (const { filter, prop } of filters) {
        if (filter(obj)) {
          if (!acc[prop]) {
            acc[prop] = [];
          }
          acc[prop].push(obj);
          break;
        }
      }
      return acc;
    }, {} as { [key in T]: any[] });
  };

//Components
export const hasScreens = has('screens');

//Routes
export const hasRoute = has('route');
export const isRoute = hasAll(['id', 'to']);

export const hasTemplate = has('tmp');
export const isTemplateType = (type: any) => templateTypeList.includes(type);
export const isTemplate = (obj: any) =>
  hasAll(['tmp', 'Component']) && isTemplateType(prop('tmp', obj));

export const hasLayout = has('layout');

export const hasToolbar = has('toolbar');

export const hasTable = has('table');

export const hasForm = (target: any) =>
  has('form', target) || has('formComponent', target);
export const isFormComponent = (target: any) =>
  hasAll(['Component', 'props'])(target) && ifPresentEq('cat', 'form')(target);

//Formats
export const isSection = hasAll(['screens', 'id']);

export const isConfigComponent = (elem: any) =>
  !isSection(elem) && !has('tmp', elem);

////Templates

export const isIndexTemplate = (target: any) =>
  hasAll(['columns', 'source'])(target) && propEq('index', 'tmp')(target);

export const isCreateTemplate = (target: any) =>
  hasAll(['form', 'empty'])(target) && propEq('create', 'tmp')(target);

export const isDetailsTemplate = (target: any) =>
  hasAll(['columns', 'source'])(target) && propEq('tmp', 'tmp')(target);

export type MapPropFromTo = [
  from: any,
  fromPath: string[],
  to: any,
  toPath: string | string[],
  def?: any,
  f?: (value: any, from?: any, to?: any) => any
];

export const mapPropFromTo = ([
  from,
  fromPath,
  to,
  toPath,
  def,
  f,
]: MapPropFromTo) => {
  const value = findFirstValueDefined(fromPath, from, def);
  if (isNotUndefined(value)) {
    const val = isUndefined(f) ? value : f(value, from, to);
    const result = assocPath(normalizePath(toPath), val, to);
    return result;
  }
  if (isNotUndefined(def)) {
    const result = assocPath(normalizePath(toPath), def, to);
    return result;
  }
  return to;
};

export const mapPropsFromTo =
  (
    maps: [
      fromPath: string[],
      toPath: string | string[],
      def?: any,
      f?: (value: any, from?: any, to?: any) => any
    ][]
  ) =>
  (from: any, to: any) => {
    const result = maps.reduce(
      (acc, [fromPath, toPath, def, f]) =>
        mapPropFromTo([from, fromPath, to, toPath, def, f]),
      to
    );
    return result;
  };
